import React from 'react';
import CoachingPlanWizard from 'components/coaching/wizard/CoachingPlanWizard';
import CoachingPlanActions from 'actions/coaching/CoachingPlanActions';
import MiniCoachingPlanDialog from 'components/coaching/wizard/MiniCoachingPlanDialog';
import buildUrl from 'components/dashboard/buildUrl';
import {browserHistory} from 'react-router';

/**
 * Higher Order Component to add the Coaching Plan Wizard to your component, see for example CoachingPlansWidget.js
 * To use it, just do
 * <code>export default withCoachingPlanWizard(YourComponent);</code>
 * instead of <code>export default YourComponent;</code>
 * With that, your React component (YourComponent) will be given two functions in the properties, namely
 * onCreatePlan and onEditPlan, which you can call on your component when you need to create or edit a plan.
 * The coaching plan wizard will be a modal dialog on the page containing your component.
 *
 * The wrapped component expects "managerId" and a function named "refreshCoachingData" in the properties, which is
 * called after a plan is created or edited (You can also pass `refreshCoachingData` when you call "onCreatePlan"
 * in order to pass a more targeted function, or if you cannot easily send a refresh via prop).
 *
 * Note: the function hideDialog is not passed to your component, but can be made to if you have such a requirement.
 * @param WrappedComponent Your React component class
 * @returns the wrapped component that you can use in place of your React component
 */
export default function withCoachingPlanWizard(WrappedComponent) {
  return class extends React.Component {
    constructor(props) {
      super(props);

      this.hideDialog = this.hideDialog.bind(this);
      this.createCoachingPlan = this.createCoachingPlan.bind(this);
      this.onCreatePlan = this.onCreatePlan.bind(this);
      this.updateCoachingPlan = this.updateCoachingPlan.bind(this);
      this.onEditPlan = this.onEditPlan.bind(this);
      this.renderWizard = this.renderWizard.bind(this);
      this.onCompleted = this.onCompleted.bind(this);
      this.goToCoachingPlans = this.goToCoachingPlans.bind(this);

      this.state = {
        wizardVisible: false,
        advancedWizardSelected: false,
        wizardPlan: null,
        preloadRep: null,
        preloadCoach: null,
        skillName: null,
        forceRenderRepSelect: false,
        savedPlan: undefined,
      };
    }

    hideDialog() {
      this.setState({
        wizardVisible: !this.state.wizardVisible,
        wizardPlan: null,
        preloadRep: null,
        preloadCoach: null,
        skillName: null,
        advancedWizardSelected: false,
        savedPlan: undefined,
        refreshCoachingData: undefined,
      });

      this.state.onHideDialog && this.state.onHideDialog();
    }

    goToCoachingPlans() {
      const {savedPlan} = this.state;
      const {adminId, managerId, coachId} = this.props;
      if (adminId || managerId || coachId) {
        const pathname = buildUrl({
          adminId,
          // if an admin is on her manager page, still go to the admin coaching plans page (no manager)
          managerId: adminId ? null : managerId,
          // it should always be the highest level user that is creating a plan, and when we are done we can
          // forget the lower-level IDs (other views)
          coachId: adminId || managerId ? null : coachId,
        });
        const query = {selectedPlanId: savedPlan.id};
        let link = {
          pathname,
          query,
        };
        browserHistory.push(link);
      }
    }

    onCompleted() {
      const {savedPlan} = this.state;
      this.props.refreshCoachingData && this.props.refreshCoachingData(savedPlan);

      if (this.state.refreshCoachingData) {
        this.state.refreshCoachingData();
        this.setState({refreshCoachingAgenda: undefined});
      }

      this.hideDialog();

      // this block executed when close coaching plan wizard from LiveCoaching page
      if (typeof this.props.onFinish === 'function') {
        this.props.onFinish(this.goToCoachingPlans);
      } else {
        this.goToCoachingPlans();
      }
    }

    onCreatePlan({coach, rep, skillName, forceRenderRepSelect, onHideDialog, refreshCoachingData}) {
      this.setState({
        wizardVisible: true,
        preloadRep: rep,
        preloadCoach: coach,
        wizardPlan: null,
        skillName,
        advancedWizardSelected: false,
        forceRenderRepSelect,
        onHideDialog,
        refreshCoachingData,
      });
    }

    /** Wizard callback. Sends new plan to the server, replaces plan in state with response. */
    async createCoachingPlan(plan) {
      const savedPlan = await CoachingPlanActions.create(plan);
      this.setState({savedPlan});
    }

    /** Wizard callback. Sends updated plan to the server, replaces plan in state with response. */
    async updateCoachingPlan(plan) {
      const savedPlan = await CoachingPlanActions.update(plan);
      this.setState({savedPlan});
    }

    onEditPlan(plan, refreshCoachingData) {
      this.setState({
        wizardVisible: true,
        wizardPlan: plan,
        refreshCoachingData,
      });
    }

    renderWizard() {
      const {preloadCoach, preloadRep, wizardPlan, advancedWizardSelected, forceRenderRepSelect} =
        this.state;

      // show the advanced wizard if..
      // - edit mode (wizardPlan present)
      // - user selected advanced mode
      const showAdvancedWizard = wizardPlan || advancedWizardSelected;

      if (showAdvancedWizard) {
        return (
          <CoachingPlanWizard
            open={this.state.wizardVisible}
            onClose={this.hideDialog}
            onCompleted={this.onCompleted}
            preloadRep={preloadRep}
            preloadedMiniWizardPlan={this.state.savedPlan}
            createCoachingPlan={this.createCoachingPlan}
            editCoachingPlan={this.updateCoachingPlan}
            managerId={this.props.managerId}
            plan={this.state.wizardPlan}
            skillToCoach={this.state.skillName}
            editMode={!!this.state.wizardPlan}
          />
        );
      }

      return (
        <MiniCoachingPlanDialog
          model={this.state.savedPlan}
          open={this.state.wizardVisible}
          onChange={savedPlan => {
            this.setState({savedPlan});
            return savedPlan;
          }}
          onClose={this.hideDialog}
          onCompleted={this.onCompleted}
          rep={preloadRep}
          coach={preloadCoach}
          skillToCoach={this.state.skillName}
          forceRenderRepSelect={forceRenderRepSelect}
          createCoachingPlan={this.createCoachingPlan}
          editCoachingPlan={this.updateCoachingPlan}
          switchToAdvanced={() =>
            this.setState({
              advancedWizardSelected: true,
            })
          }
        />
      );
    }

    render() {
      return (
        <div>
          <WrappedComponent
            {...this.props}
            onCreatePlan={this.onCreatePlan}
            onEditPlan={this.onEditPlan}
          />
          {this.state.wizardVisible && this.renderWizard()}
        </div>
      );
    }
  };
}
